import type { HelpContent } from './types';

export const projectPlHelp: HelpContent = {
  pageTitle: 'Project P&L Report',
  description: 'Review profit and loss performance across your project portfolio, with revenue, cost, gross profit, and margin data at both project and portfolio level.',

  functionality: {
    overview: 'The Project P&L Report provides a financial performance view across your portfolio. It shows revenue, costs, gross profit, and margin for each project, helping you understand which projects are performing well and where financial performance needs attention.',

    keyFeatures: [
      {
        label: 'Portfolio P&L summary',
        detail: 'KPI cards show total revenue, total costs, gross profit, and overall margin across the portfolio at a glance.',
      },
      {
        label: 'Project-level P&L',
        detail: 'See revenue, costs, gross profit, and margin broken down by project, with health indicators to flag underperforming projects.',
      },
      {
        label: 'GP calculation method',
        detail: 'The GP calculation method used in the report is configurable in Settings, allowing your organisation to apply the GP methodology that best reflects your business.',
      },
      {
        label: 'Health indicators',
        detail: 'Each project is colour-coded based on margin health thresholds, making it easy to spot projects with low or negative margins.',
      },
      {
        label: 'Search and filter',
        detail: 'Search by project name or client, and filter by financial health to focus on specific projects.',
      },
      {
        label: 'Export to Excel',
        detail: 'Export the full P&L report to Excel for use in financial reporting or board packs.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'KPI summary cards',
        description: 'Displays portfolio-level totals: total revenue, total costs, gross profit, and overall gross margin percentage.',
      },
      {
        name: 'GP method indicator',
        description: 'Shows which GP calculation method is currently applied to the report. Configurable in Settings by an Administrator.',
      },
      {
        name: 'Search bar',
        description: 'Filter the project list in real time by project name or client name.',
      },
      {
        name: 'Financial health filter',
        description: 'Filter projects by their financial health status to focus on projects with margin concerns.',
      },
      {
        name: 'P&L table',
        description: 'Lists all projects with columns for project name, client, project manager, revenue, costs, gross profit, gross margin percentage, and financial health status.',
      },
      {
        name: 'Margin health indicator',
        description: 'A colour-coded indicator showing the financial health of the project based on its gross margin, using thresholds configured in Settings.',
      },
      {
        name: 'Export button',
        description: 'Downloads the P&L report as an Excel file.',
      },
    ],

    useCases: [
      'Reviewing financial performance across the portfolio before a finance or board meeting.',
      'Identifying projects with low or negative margins that need financial review.',
      'Comparing revenue and cost performance across projects.',
      'Exporting P&L data for use in financial reports or board packs.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Project P&L Report loads with all projects that have financial data. Use the KPI cards for a portfolio-level summary, then review individual project rows for detail.',
    steps: [
      {
        title: 'How do I identify underperforming projects?',
        items: [
          { text: 'Use the financial health filter to focus on projects with At Risk or Critical margin health.' },
          { text: 'Look for projects with a red or amber margin health indicator in the table.' },
          { text: 'Click into the project to navigate to its full Project Details page for further investigation.' },
        ],
      },
      {
        title: 'How do I export the report?',
        items: [
          { text: 'Apply any filters you need.' },
          { text: 'Click the Export button in the page header.' },
          { text: 'The report downloads as an Excel file.' },
        ],
      },
    ],
    proTips: [
      'The GP calculation method affects how gross profit is calculated across all projects in this report. If the figures don\'t align with your finance team\'s expectations, check the GP method setting with your Administrator.',
      'Use this report alongside the Budget Variance Report to get a complete picture of both cost performance and financial profitability.',
    ],
  },

  faq: [
    {
      question: 'What GP calculation method is used?',
      answer: 'The GP calculation method is configured by an Administrator in Settings. The method currently applied is shown in the report header. Contact your Administrator if you need to change it.',
    },
    {
      question: 'Why does a project show no financial data?',
      answer: 'A project will only show P&L data if revenue and cost information has been recorded. If a project is missing data, check that budget and financial entries have been set up in the Project Details page.',
    },
    {
      question: 'Can I edit financial figures from this report?',
      answer: 'No. The P&L Report is read-only. To update financial data, navigate to the relevant project\'s billing or budget tabs in Project Details.',
    },
    {
      question: 'How are the margin health thresholds set?',
      answer: 'Margin health thresholds are configured by an Administrator in Settings. They define the margin percentage at which a project moves between health statuses.',
    },
  ],

  relatedPages: [
    { label: 'Projects', path: '/projects' },
    { label: 'Profitability Report', path: '/reports/profitability' },
    { label: 'Budget Variance Report', path: '/reports/budget-variance' },
  ],
};
